import { Colors, Img, Language, Lightning, Registry, Router, Storage, Utils } from "@lightningjs/sdk";
import { TranslatableText } from "../../components/data-display/TranslatableText/TranslatableText";
import theme from "../../lib/theme";
import { Button } from "../../components/input/Button/Button";
import { createPostTvSubscription } from "../../api/mutations";
import { Loading } from "../../components/feedback/Loading/Loading";
import {STORAGE_KEYS, deviceId, isPostTvAndroid, isPostTvLinux} from "../../lib/utils";
import configs from "../../lib/configs";
import posthog from "posthog-js";
import { Error } from "../../components/feedback/Error/Error";
import { setPinInputCallbacks } from "../PinInput/PinInput";

interface PostTvSubscriptionTemplateSpec extends Lightning.Component.TemplateSpec {
    Error: typeof Error;
    Loading: typeof Loading;
    Box: {
        Background: object;
        Content: {
            LogoContainer: {
                Logo: object;
            };
            DescriptionText: typeof TranslatableText;
            SubscriptionBox: {
                Background: object;
                SubscriptionName: typeof TranslatableText;
                SubscriptionPrice: typeof TranslatableText;
            };
            OfferedSubscriptionBox: {
                Background: object;
                SubscriptionName: typeof TranslatableText;
                SubscriptionPrice: typeof TranslatableText;
            };
            CommitmentText: typeof TranslatableText;
            Prices: any;
            Actions: {
                Cancel: typeof Button;
                Confirm: typeof Button;
            };
            Terms: typeof TranslatableText;
        };
    };
}

export class PostTvSubscription
    extends Lightning.Component<PostTvSubscriptionTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<PostTvSubscriptionTemplateSpec>
{
    _loading = false;
    _activePage: undefined | string = "home";
    _items: any[] = [];

    _loginTimeout: undefined | number;

    _priceIndex = 0;
    _prices: null | any[] = null;

    readonly Loading = this.getByRef("Loading")!;
    readonly Error = this.getByRef("Error")!;
    readonly Box = this.getByRef("Box")!;
    readonly Content = this.Box.getByRef("Content")!;
    readonly DescriptionText = this.Content.getByRef("DescriptionText")!;
    readonly SubscriptionBox = this.Content.getByRef("SubscriptionBox")!;
    readonly SubscriptionName = this.SubscriptionBox.getByRef("SubscriptionName")!;
    readonly SubscriptionPrice = this.SubscriptionBox.getByRef("SubscriptionPrice")!;
    readonly OfferedSubscriptionBox = this.Content.getByRef("OfferedSubscriptionBox")!;
    readonly OfferedSubscriptionName = this.OfferedSubscriptionBox.getByRef("SubscriptionName")!;
    readonly OfferedSubscriptionPrice = this.OfferedSubscriptionBox.getByRef("SubscriptionPrice")!;
    readonly CommitmentText = this.Content.getByRef("CommitmentText")!;
    readonly Prices = this.Content.getByRef("Prices")!;
    readonly Actions = this.Content.getByRef("Actions")!;
    readonly Cancel = this.Actions.getByRef("Cancel")!;
    readonly Confirm = this.Actions.getByRef("Confirm")!;
    readonly Terms = this.Content.getByRef("Terms")!;

    static override _template(): Lightning.Component.Template<PostTvSubscriptionTemplateSpec> {
        return {
            zIndex: 40,
            w: theme.layout.w,
            h: theme.layout.h,
            x: 0,
            y: 0,
            rect: true,
            color: Colors(theme.colors.bg.overlay).alpha(0.7).get(),
            alpha: 0,
            collision: true,
            Box: {
                w: 1180,
                h: 730,
                mount: 0.5,
                x: (w) => w / 2,
                y: (h) => h / 2,
                Background: {
                    x: -1,
                    y: -1,
                    texture: Lightning.Tools.getRoundRect(
                        1180,
                        730,
                        12,
                        0,
                        undefined,
                        true,
                        Colors(theme.colors.bg.secondary).get()
                    )
                },
                Content: {
                    w: 1180 - 24 * 2,
                    h: (h) => h,
                    flex: {
                        direction: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingLeft: 24,
                        paddingRight: 24
                    },
                    LogoContainer: {
                        h: 180,
                        w: 300,
                        Logo: {
                            mount: 0.5,
                            x: (w) => w / 2,
                            y: (h) => h / 2,
                            texture: Img(Utils.asset("images/logo.png")).contain(theme.logo.w, theme.logo.h)
                        }
                    },
                    DescriptionText: {
                        flexItem: { marginBottom: 12, marginTop: 24 },
                        type: TranslatableText,
                        translate: {
                            key: "subscription.posttv",
                            value: ""
                        },
                        text: {
                            textAlign: "center",
                            fontFace: "SemiBold",
                            fontSize: 24,
                            lineHeight: 32,
                            wordWrap: true,
                            wordWrapWidth: 1100,
                            textColor: Colors(theme.colors.text.primary).get()
                        }
                    },
                    SubscriptionBox: {
                        flexItem: { marginBottom: 12 },
                        w: 740,
                        h: 70,
                        Background: {
                            w: 740,
                            h: 70,
                            texture: Lightning.Tools.getRoundRect(
                                740,
                                70,
                                8,
                                3,
                                Colors(theme.colors.focus).alpha(0.3).get(),
                                true,
                                Colors(theme.colors.bg.primary).alpha(0.5).get()
                            )
                        },
                        SubscriptionName: {
                            type: TranslatableText,
                            x: 370,
                            y: 40,
                            mount: 0.5,
                            translate: {
                                key: "",
                                value: ""
                            },
                            text: {
                                textAlign: "center",
                                fontFace: "SemiBold",
                                fontSize: 24,
                                lineHeight: 30,
                                wordWrap: false,
                                textColor: Colors(theme.colors.text.primary).get()
                            }
                        },
                        SubscriptionPrice: {
                            type: TranslatableText,
                            x: 370,
                            y: 40,
                            mount: 0.5,
                            translate: {
                                key: "",
                                value: ""
                            },
                            text: {
                                textAlign: "center",
                                fontFace: "SemiBold",
                                fontSize: 24,
                                lineHeight: 30,
                                textColor: Colors(theme.colors.text.primary).get()
                            }
                        }
                    },
                    OfferedSubscriptionBox: {
                        alpha: 0,
                        flexItem: false,
                        w: 740,
                        h: 70,
                        Background: {
                            w: 740,
                            h: 70,
                            texture: Lightning.Tools.getRoundRect(
                                740,
                                70,
                                8,
                                3,
                                Colors(theme.colors.focus).alpha(0.3).get(),
                                true,
                                Colors(theme.colors.bg.primary).alpha(0.5).get()
                            )
                        },
                        SubscriptionName: {
                            type: TranslatableText,
                            x: 370,
                            y: 40,
                            mount: 0.5,
                            translate: {
                                key: "",
                                value: ""
                            },
                            text: {
                                textAlign: "center",
                                fontFace: "SemiBold",
                                fontSize: 24,
                                lineHeight: 30,
                                wordWrap: false,
                                textColor: Colors(theme.colors.text.primary).get()
                            }
                        },
                        SubscriptionPrice: {
                            type: TranslatableText,
                            x: 370,
                            y: 40,
                            mount: 0.5,
                            translate: {
                                key: "",
                                value: ""
                            },
                            text: {
                                textAlign: "center",
                                fontFace: "SemiBold",
                                fontSize: 24,
                                lineHeight: 30,
                                textColor: Colors(theme.colors.text.primary).get()
                            }
                        }
                    },
                    CommitmentText: {
                        flexItem: { marginBottom: 10 },
                        type: TranslatableText,
                        translate: {
                            key: "subscription.posttv.commitment",
                            value: ""
                        },
                        text: {
                            textAlign: "center",
                            fontFace: "Medium",
                            fontSize: 16,
                            lineHeight: 16,
                            wordWrap: true,
                            wordWrapWidth: 1100,
                            textColor: Colors(theme.colors.text.primary).get()
                        }
                    },
                    Prices: {
                        alpha: 0,
                        collision: true,
                        w: (w) => w,
                        flex: {
                            direction: "column",
                            alignItems: "center",
                            justifyContent: "space-around"
                        }
                    },
                    Actions: {
                        collision: true,
                        flexItem: { marginTop: 36, marginBottom: 36 },
                        w: 410,
                        flex: {
                            direction: "row",
                            alignItems: "flex-start",
                            justifyContent: "space-between"
                        },
                        Cancel: {
                            type: Button,
                            zIndex: 50,
                            label: "actions.cancel",
                            w: 185,
                            signals: {
                                handleClick: "_handleCancel"
                            }
                        },
                        Confirm: {
                            type: Button,
                            zIndex: 50,
                            label: "actions.subscribe",
                            w: 185,
                            signals: {
                                handleClick: "_handleConfirm"
                            }
                        }
                    },
                    Terms: {
                        flexItem: { marginTop: 24, marginBottom: 36, alignSelf: "flex-end", marginRight: 48 },
                        type: TranslatableText,
                        translate: {
                            key: "subscription.posttv.terms",
                            value: ""
                        },
                        text: {
                            textAlign: "center",
                            fontFace: "Medium",
                            fontSize: 18,
                            lineHeight: 20,
                            wordWrap: true,
                            wordWrapWidth: 1100,
                            textColor: Colors(theme.colors.text.primary).get()
                        }
                    }
                }
            },
            Error: {
                type: Error,
                alpha: 0,
                zIndex: 60
            },
            Loading: {
                type: Loading,
                alpha: 0,
                delay: 1,
                zIndex: 60
            }
        };
    }

    override _active() {
        const priceData = Storage.get("subscriptionData");
        const price = priceData?.price;
        const relatedSubs = priceData?.relatedSubscriptions?.map((s: any) => s.name).join(", ");
        const totalRelatedPrice = priceData?.relatedSubscriptions?.reduce(
            (sum: number, s: any) => sum + parseFloat(s.price || 0),
            0
        );
        const prices = priceData?.prices || [];
        const hasRelatedSubs = relatedSubs && relatedSubs.length > 0;
        const displayMode = (configs.settings as any)?.subscriptionDisplayMode || "default";

        this._prices = prices;

        // Default mode: Show price in description text, hide boxes
        const translateValue = {
            appName: configs.metadata?.name,
            product: priceData?.name || configs.metadata?.name,
            relatedProducts: relatedSubs,
            currentPrice: totalRelatedPrice ? totalRelatedPrice.toFixed(2) + "€" : "",
            value: (price || "") + "€"
        };

        let descriptionKey: string;
        if (!price) {
            descriptionKey = "subscription.posttv.mytv";
        } else if (hasRelatedSubs) {
            if (isPostTvLinux()) {
                const linuxKey = "subscription.posttv.linux.hasRelated";
                const linuxText = Language.translate(linuxKey, translateValue);
                descriptionKey = linuxText !== linuxKey ? linuxKey : "subscription.posttv.hasRelated";
            } else {
                descriptionKey = "subscription.posttv.hasRelated";
            }
        } else {
            if (isPostTvLinux()) {
                const linuxKey = "subscription.posttv.linux";
                const linuxText = Language.translate(linuxKey, translateValue);
                descriptionKey = linuxText !== linuxKey ? linuxKey : "subscription.posttv";
            } else {
                descriptionKey = "subscription.posttv";
            }
        }

        this.DescriptionText.patch({
            translate: {
                key: descriptionKey,
                value: translateValue
            }
        });

        // Check display mode
        if (displayMode === "boxes") {
            // Show subscription boxes
            this.SubscriptionBox.patch({
                alpha: 1,
                flexItem: { marginBottom: 12 }
            });

            // Update subscription box - Option 1: show new subscription, Option 2: show current subscription
            if (hasRelatedSubs) {
                // Option 2: Show current subscription in first box and offered subscription in second box
                const currentPrice = totalRelatedPrice ? totalRelatedPrice.toFixed(2) : "";
                this.SubscriptionName.patch({
                    translate: {
                        key: "subscription.posttv.box",
                        value: {
                            product: relatedSubs.replace(" v2", ""),
                            price: currentPrice.replace(".", ",")
                        }
                    }
                });
                this.SubscriptionPrice.patch({
                    alpha: 0
                });

                // Show offered subscription box and include it in flex layout
                this.OfferedSubscriptionBox.patch({
                    alpha: 1,
                    flexItem: { marginBottom: 12 }
                });
                this.OfferedSubscriptionName.patch({
                    translate: {
                        key: "subscription.posttv.box",
                        value: {
                            product: (priceData?.name || configs.metadata?.name).replace(" v2", ""),
                            price: (price || "").replace(".", ",")
                        }
                    }
                });
                this.OfferedSubscriptionPrice.patch({
                    alpha: 0
                });

                // Update button label to "Hinzubuchen"
                this.Confirm.patch({
                    label: "actions.add"
                });
            } else {
                // Option 1: Show new subscription with price in one row
                this.SubscriptionName.patch({
                    translate: {
                        key: "subscription.posttv.box",
                        value: {
                            product: (priceData?.name || configs.metadata?.name).replace(" v2", ""),
                            price: (price || "").replace(".", ",")
                        }
                    }
                });
                this.SubscriptionPrice.patch({
                    alpha: 0
                });

                // Hide offered subscription box and exclude it from flex layout
                this.OfferedSubscriptionBox.patch({
                    alpha: 0,
                    flexItem: false
                });

                // Update button label to "Abonnieren"
                this.Confirm.patch({
                    label: "actions.subscribe"
                });
            }
        } else {
            // Hide both subscription boxes
            this.SubscriptionBox.patch({
                alpha: 0,
                flexItem: false
            });
            this.OfferedSubscriptionBox.patch({
                alpha: 0,
                flexItem: false
            });
        }

        // Update commitment text
        this.CommitmentText.patch({
            skipEmpty: true,
            translate: {
                key: "subscription.posttv.commitment",
                value: {}
            }
        });

        this.Terms.patch({
            skipEmpty: true,
            translate: {
                key: "subscription.posttv.terms",
                value: {
                    appName: configs.metadata?.name,
                    product: priceData?.name || configs.metadata?.name,
                    relatedProducts: relatedSubs,
                    value: configs.settings.terms
                }
            }
        });

        if (prices.length > 1) {
            this.Prices.children = priceData?.prices.map((price: any) => ({
                type: Button,
                zIndex: 50,
                label: `${price.name} ${(price.price || "") + "€"}`,
                w: 550,
                flexItem: {
                    marginBottom: 24
                },
                signals: {
                    handleClick: "_handleCancel"
                }
            }));

            this.Prices.patch({
                flexItem: { marginTop: 36 },
                alpha: 1,
                h: 220
            });

            this.Actions.patch({
                alpha: 0,
                flexItem: { marginTop: 0, marginBottom: 0 }
            });

            this._setState("PricesState");

            return;
        } else {
            this.Actions.patch({
                h: 56
            });
        }

        // this._setState("CloseState");
    }

    override _getFocused(): any {
        return this.Confirm;
    }

    override _focus() {
        this.patch({
            Box: {
                alpha: 1
            },
            smooth: {
                alpha: 1
            }
        });

        this.DescriptionText.patch({
            translate: {
                key: "subscription.posttv",
                value: {
                    appName: configs.metadata?.name,
                    value: (Storage.get("subscriptionData")?.price || "") + "€"
                }
            }
        });

        if (this._loading) {
            this._setState("LoadingState");
        } else {
            this._setState("CloseState");
        }
    }

    override _unfocus() {
        this.patch({
            smooth: {
                alpha: 0
            }
        });
    }

    override _detach() {
        if (this._loginTimeout) {
            Registry.clearTimeout(this._loginTimeout);
            this._loginTimeout = undefined;
        }
    }

    override _handleUp() {
        // needed to prevent focus on page
    }

    override _handleLeft() {
        // needed to prevent focus on page
        this._setState("CloseState");
    }

    override _handleRight() {
        this._setState("ConfirmState");
    }

    override _handleEnter() {
        const pinRequired = configs.useParentalPin && isPostTvAndroid();
        if (pinRequired) {
            setPinInputCallbacks(
                () => {
                    // PIN verified — re-focus this widget and proceed with subscription
                    Router.focusWidget("PostTvSubscription");
                    this._doSubscribe();
                },
                () => {
                    // PIN cancelled — re-focus this widget
                    Router.focusWidget("PostTvSubscription");
                }
            );
            Router.focusWidget("PinInput");
            return;
        }
        this._doSubscribe();
    }

    _doSubscribe() {
        this._setState("LoadingState");
        this._loading = true;
        (async () => {
            try {
                const { token, ...user } = await createPostTvSubscription(
                    this._prices?.length ? this._prices[this._priceIndex]?.id : undefined
                );
                Storage.set(STORAGE_KEYS.user, user);
                Storage.set(STORAGE_KEYS.token, token);

                if (user.subscribed) {
                    posthog.capture("SuccessfulAuth", {
                        token: await deviceId()
                    });

                    this._loginTimeout = Registry.setTimeout(() => {
                        this._loginTimeout = undefined;

                        this._setState("");
                        this._loading = false;

                        if (this.hasFocus()) {
                            Router.focusPage();
                        }
                    }, 30000);
                } else {
                    this._setState("");
                    this._loading = false;
                }
            } catch (e) {
                this._setState("ErrorState");
                this._loading = false;
            }
        })();
    }

    override _handleBack() {
        Router.focusPage();
    }

    override _handleDown() {
        //
    }

    _handleClick() {
        this._handleBack();
    }

    static override _states() {
        return [
            class PricesState extends this {
                override _getFocused() {
                    return this.Prices.children[this._priceIndex];
                }

                override $enter() {
                    this._priceIndex = 0;
                }

                override _handleUp() {
                    this._priceIndex = Math.max(0, this._priceIndex - 1);

                    this._refocus();
                }

                override _handleDown() {
                    this._priceIndex = Math.min(this._prices!.length - 1, this._priceIndex + 1);

                    this._refocus();
                }
            },
            class CloseState extends this {
                override _getFocused() {
                    return this.Cancel;
                }

                override _handleEnter() {
                    console.log("ENTER");

                    Router.focusPage();
                }

                override _handleRight() {
                    this._setState("");
                }
            },
            class ErrorState extends this {
                override $enter() {
                    this.Box.patch({
                        alpha: 0
                    });
                    this.Loading.patch({
                        alpha: 0
                    });

                    this.Error.patch({
                        alpha: 1
                    });
                }

                override $exit() {
                    this.Error.patch({
                        alpha: 0
                    });
                }

                override _getFocused() {
                    return this.Error;
                }

                override _handleEnter() {
                    Router.focusPage();
                }
            },
            class LoadingState extends this {
                override $enter() {
                    this.Box.patch({
                        alpha: 1
                    });
                    this.Loading.patch({
                        alpha: 1
                    });

                    this.Error.patch({
                        alpha: 0
                    });
                }

                override $exit() {
                    this.Loading.patch({
                        alpha: 0
                    });
                }

                override _getFocused() {
                    return this.Loading;
                }

                override _handleEnter() {}
                override _handleLeft() {}
                override _handleRight() {}
                override _handleUp() {}
                override _handleDown() {}
                override _handleClick() {}
            }
        ];
    }
}
